import { useFormContext } from 'react-hook-form';
import { Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { useFieldMaterialUIStyles } from 'src/presentations/components/molecules/fields/useFieldMaterialUIStyles';
import { RhfMuiNumericField, RhfMuiTextArea } from 'src/presentations/components/molecules/fields/rhf-mui-fields';
import { InvoiceSummaryDto } from 'src/generated/services/TFinancialApi';
import { DisputeAgreement } from 'src/utils/options';
import { nameofFactory } from 'src/utils/nameofFactory';
import { IDisputeFormValues } from './types';
import { ModernUploadDocument } from './ModernUploadDocument';
import { useEffect } from 'react';
import { formatNumber } from 'src/utils/utility';

interface IFormResolutionProps {
  invoiceSummary: InvoiceSummaryDto;
}

const nameof = nameofFactory<IDisputeFormValues>();

export const FormResolution: React.FC<IFormResolutionProps> = (props) => {
  const { invoiceSummary } = props;
  const { classes } = useFieldMaterialUIStyles();
  const { watch, setValue } = useFormContext<IDisputeFormValues>();
  const agreementType = watch('agreementType');
  const amountOption = watch('amountOption');
  const otherAmount = watch('otherAmount');

  // Get amounts from invoice summary
  const fullInvoiceAmount = invoiceSummary?.invoiceAmount?.afterTax;
  const deviationsAmount = invoiceSummary?.invoiceAmountDeviation?.afterTax;
  const currency = invoiceSummary?.invoiceDispute?.currency;

  // Handle radio button change
  const handleAmountOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedOption = event.target.value as 'FULL_INVOICE' | 'DEVIATIONS' | 'OTHER';
    setValue('amountOption', selectedOption);

    // Clear otherAmount field when switching AWAY from OTHER option
    if (selectedOption !== 'OTHER') {
      setValue('otherAmount', '');
    }
  };

  // Set default option on mount
  useEffect(() => {
    if (!amountOption && agreementType === DisputeAgreement.ACCEPT_INVOICE_WITH_CREDIT_NOTE) {
      setValue('amountOption', 'OTHER');
    }
  }, [agreementType, amountOption, setValue]);

  return (
    <div>
      <h3 className='text-16 xl:text-20 leading-20 xl:leading-24 mb-4 font-bold'>Resolution</h3>
      <p className='mb-24 text-blue_grey-90 text-14 xl:text-16 leading-18'>
        {agreementType === 'ACCEPT_INVOICE_WITH_CREDIT_NOTE' && 'Specify the reason for accepting this invoice with credit note:'}
        {agreementType === 'REJECT_INVOICE' && 'Specify the reason for rejection:'}
        {agreementType === 'CANCEL_DISPUTE' && 'Specify the reason for the cancelation:'}
        {!agreementType && 'Please document and classify the outcome of your exchange with your Service Provider to resolve this flagged invoice'}
      </p>
      <div className='mb-24'>
        <RhfMuiTextArea name={nameof('reason')} className={classes.inputField} placeholder='Input Description' fullWidth required />
      </div>
      {/* Always show credit note input for Accept option */}
      {agreementType === DisputeAgreement.ACCEPT_INVOICE_WITH_CREDIT_NOTE && (
        <div className='mb-24'>
          <p className='mb-8 text-blue_grey-30 text-14 xl:text-16 leading-18'>Please indicate the expected amount to be covered by the credit note:</p>

          <RadioGroup value={amountOption || 'OTHER'} onChange={handleAmountOptionChange} row className='gap-30 items-center'>
            <FormControlLabel
              value='FULL_INVOICE'
              control={<Radio sx={{ padding: '5px' }} />}
              label={
                <span className='text-14 xl:text-16 whitespace-nowrap'>
                  Full invoice amount:{' '}
                  <span className='font-semibold'>
                    {currency} {fullInvoiceAmount !== undefined ? formatNumber(fullInvoiceAmount) : 'N/A'}
                  </span>
                </span>
              }
              sx={{ marginRight: 0 }}
            />

            <FormControlLabel
              value='DEVIATIONS'
              control={<Radio sx={{ padding: '5px' }} />}
              label={
                <span className='text-14 xl:text-16 whitespace-nowrap'>
                  Deviations amount:{' '}
                  <span className='font-semibold'>
                    {currency} {deviationsAmount !== undefined ? formatNumber(deviationsAmount) : 'N/A'}
                  </span>
                </span>
              }
              sx={{ marginRight: 0 }}
            />

            <div className='flex items-center gap-8'>
              <FormControlLabel
                value='OTHER'
                control={<Radio sx={{ padding: '5px' }} />}
                label={<span className='text-14 xl:text-16 whitespace-nowrap'>Other amount</span>}
                sx={{ marginRight: 0 }}
              />

              <div className='text-14 mx-10 xl:text-16 text-blue_grey-30'>{currency}</div>
              <div className='w-[300px]'>
                <RhfMuiNumericField
                  name={nameof('otherAmount')}
                  className={classes.inputField}
                  placeholder='Total amount of credit note (after tax)'
                  multiline={false}
                  rows={1}
                  fullWidth
                  size='small'
                  required
                  disabled={!!amountOption && amountOption !== 'OTHER'}
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: '#FFF',
                      fontSize: '14px',
                      color: '#000',
                      '& fieldset': {
                        borderColor: '#BFBFBF',
                      },
                      '&:hover fieldset': {
                        borderColor: '#00992B',
                        borderWidth: '1px !important',
                      },
                      '& input': {
                        backgroundColor: '#FFF',
                        color: '#000',
                        fontSize: '14px',
                      },
                      '& input::placeholder': {
                        color: '#9F9F9F',
                        opacity: 1,
                      },
                    },
                  }}
                />
              </div>
            </div>
          </RadioGroup>
        </div>
      )}

      <ModernUploadDocument invoiceSummary={invoiceSummary} attachmentType='CREDIT_NOTE' />
    </div>
  );
};
